import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import merge from 'lodash/merge';
import transform from 'lodash/transform';

const MAX_DEPTH = 1;

class CommonUtil {
  /**
   * Deep diff between two objects, using lodash
   * @param  {object} source    The first object to compare
   * @param  {object} comparand The other object to compare
   * @return {object}           Returns a new object with the diff, or null if empty
   */
  static getDiff(source, comparand) {
    function changes(source, comparand) {
      return transform(source, (result, value, key) => {
        if (!isEqual(value, comparand[key])) {
          result[key] =
            isObject(value) && isObject(comparand[key]) ? changes(value, comparand[key]) : value;
        }
      });
    }
    const sourceDiff = changes(source, comparand);
    const comparandDiff = changes(comparand, source);
    return merge(sourceDiff, comparandDiff);
  }

  /**
   * Check if two objects are equal
   * @param  {object} source    The first object to compare
   * @param  {object} comparand The other object to compare
   * @param  {object} depth     Current depth of comparison (for recursion)
   * @return {boolean}          Returns true if the objects are equal, false otherwise
   */
  static areObjectsEqual(source, comparand, depth = 0) {
    // Account for null inputs (e.g., from a stateless component)
    const isSourceNil = isNil(source);
    const isComparandNil = isNil(comparand);
    if (isSourceNil && isComparandNil) {
      return true;
    } else if (isSourceNil || isComparandNil) {
      return false;
    }

    const sourceKeys = Object.keys(source);
    const comparandKeys = Object.keys(comparand);
    const lengthsMismatch = sourceKeys.length !== comparandKeys.length;
    const keysMismatch =
      lengthsMismatch || JSON.stringify(sourceKeys.sort()) !== JSON.stringify(comparandKeys.sort());

    // If the object keys don't match, we already know the objects don't match
    if (keysMismatch) {
      return false;
    }
    // Otherwise, check each prop, and recurse as needed
    for (const prop of sourceKeys) {
      const sourceValue = source[prop];
      const comparandValue = comparand[prop];

      // Account for NaN inputs
      const isSourceValueNaN = Number.isNaN(sourceValue);
      const isComparandValueNaN = Number.isNaN(comparandValue);
      if (isSourceValueNaN && isComparandValueNaN) {
        console.warn(
          `React rendering danger detected: the property ${prop} source and comparand is NaN, NaN === NaN is always false, so we are skipping this property`,
        );
        continue;
      } else if (isSourceValueNaN || isComparandValueNaN) {
        console.warn(`React rendering danger detected: the property ${prop} is NaN`);
        return false;
      }

      // Account for null inputs
      const isSourceValueNil = isNil(sourceValue);
      const isComparandValueNil = isNil(comparandValue);
      if (isSourceValueNil && isComparandValueNil) {
        continue;
      } else if (isSourceValueNil || isComparandValueNil) {
        return false;
      }

      const areFunctions = isFunction(sourceValue) && isFunction(comparandValue);
      // Skip further checks, to maximize efficiency
      if (areFunctions) {
        const areSameFunction =
          sourceValue === comparandValue || sourceValue.toString() === comparandValue.toString();
        if (!areSameFunction) {
          return false;
        }
        continue;
      }

      const areDates = isDate(sourceValue) && isDate(comparandValue);
      // Skip further checks, to maximize efficiency
      if (areDates) {
        if (!isEqual(sourceValue, comparandValue)) {
          return false;
        }
        continue;
      }

      // Deal with mismatched values
      if (sourceValue !== comparandValue) {
        // If we're dealing with a difference in primitives,
        // in arrays, or between objects and arrays, return false

        const areObjects = typeof sourceValue === 'object' && typeof comparandValue === 'object';
        // Skip further checks, to maximize efficiency
        if (!areObjects) {
          return false;
        }

        const isSourceValueArray = isArray(sourceValue);
        const isCompValueArray = isArray(comparandValue);
        const areValuesArrays = isSourceValueArray && isCompValueArray;
        const arraysMismatch = areValuesArrays
          ? !CommonUtil.areArraysStrictlyEqual(sourceValue, comparandValue)
          : false;
        // Skip further checks, to maximize efficiency
        if (areValuesArrays) {
          if (arraysMismatch) {
            return false;
          }
          continue;
        }

        const isTypeMismatch =
          (isSourceValueArray && !isCompValueArray) || (!isSourceValueArray && isCompValueArray);
        // Skip further checks, to maximize efficiency
        if (isTypeMismatch) {
          return false;
        }

        // ELSE (be default, due to the `return` calls)
        // These are objects, but not a ref to the SAME object
        // If we haven't reached max depth, recurse!
        if (depth < MAX_DEPTH) {
          const areObjectsPlain = isPlainObject(sourceValue) && isPlainObject(comparandValue);
          if (areObjectsPlain) {
            const areObjectsEqual = CommonUtil.areObjectsEqual(
              sourceValue,
              comparandValue,
              depth + 1,
            );
            if (!areObjectsEqual) {
              return false;
            }
          }
        }
      }
    }

    // If all diff checks pass, return true
    return true;
  }

  /**
   * Does a shallow, unsorted comparison of 2 arrays, to see if they are
   * equal, at a shallow, unsorted level (meaning object references have
   * to be the same). This is most useful for quickly determining if a
   * component should update.
   * @param  {array} source     The first array to compare
   * @param  {array} comparand  The other array to compare
   * @return {boolean}          Returns true if the arrays are equal, false otherwise
   */
  static areArraysStrictlyEqual(source, comparand) {
    if (source === comparand) {
      return true;
    }
    if (source == null || comparand == null) {
      return false;
    }
    if (source.length !== comparand.length) {
      return false;
    }

    // NOTE: We could sort the arrays, but for the purposes of quicker
    // diffs, we're not too worried about "scrambled matches"
    for (let i = 0; i < source.length; ++i) {
      if (source[i] !== comparand[i]) {
        if (isObject(source[i]) || isObject(comparand[i])) {
          // TODO: Why do we ignore objects that don't match!?
          continue;
        } else {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Simplifies an array objects to just an array of properties and then compares
   * the simplified array of properties for equality by value. For example, if object has many properties
   * but `id` is unique per object, then using this function compares simply two arrays of `id`.
   * @param {readonly Object[]} source     The first array to compare
   * @param {readonly Object[]} comparand  The other array to compare
   * @param {String}   prop       The name of the prop to compare in each object
   * @param {Boolean}  Returns true if the arrays equal by the property, false otherwise
   */
  static areArraysOfObjectsEqualByProp(source, comparand, prop) {
    // Basic comparisons
    if (source === comparand) {
      return true;
    }
    if (source == null || comparand == null) {
      return false;
    }
    if (source.length !== comparand.length) {
      return false;
    }
    // Real comparison logic
    const getObjProp = (obj) => obj[prop];
    const reducedSource = source.map(getObjProp);
    const reducedComparand = comparand.map(getObjProp);
    for (let i = 0; i < reducedSource.length; ++i) {
      if (reducedSource[i] !== reducedComparand[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Go through the collection and set each object's key with same value
   * @param {[Object]} collection
   * @param {string} key
   * @param {*} value
   */
  // static bulkSet(collection, key, value) {
  //   collection.forEach(object => {
  //     set(object, [key], value);
  //   });
  // }

  /**
   * Map objects array to object of objects
   * @param {array} collection
   * @param {string} key
   * @param {boolean} clone
   */
  static mapCollectionToObject({ collection, key, clone }) {
    const newObject = {};
    collection.forEach((object) => {
      if (clone) {
        newObject[object[key]] = cloneDeep(object);
      } else {
        newObject[object[key]] = object;
      }
    }, key);

    return newObject;
  }

  static mapObjectToCollection(object, includeKey = false) {
    return Object.keys(object).map((key) => {
      const value = object[key];
      if (includeKey && typeof value === 'object') {
        value.mappingKey = key;
        return value;
      }
      return value;
    });
  }

  static mapObjectToArray(object, key) {
    return Object.keys(object).map((objectKey) => {
      const value = object[objectKey][key];
      return value;
    });
  }

  /**
   * Get the value of a specific key in an object, via the key name
   * @param  {object}  object The source object to find the key:value pair in
   * @param  {string}  key    The name of the key we're looking for
   * @return {boolean}        Returns the value of that key, or `undefined`
   */
  static findKeyValueInObject({ object, key }) {
    const props = Object.keys(object);
    for (const prop of props) {
      const value = object[prop];
      if (prop === key) {
        return value;
      } else if (isPlainObject(value)) {
        const output = this.findKeyValueInObject({ object: value, key });
        if (output) {
          return output;
        }
      }
    }
    return undefined;
  }

  /**
   * Strips a CSS unit from a "number" (of type `string`)
   * @param  {number/string}  value  A string containing a number and, possibly, a unit (e.g., 'px')
   * @return {number}                Returns an integer or floating point number
   */
  static CSSToNumber(value) {
    return parseFloat(value, 10);
  }

  /**
   * Trim leading spaces of text input
   * @param {string}  value   The text input that needs to be trimed
   * @return {string}         Return the trimmed string
   */
  static trimLeadingSpaces(value) {
    return value.replace(/^\s+/g, '');
  }

  static isEmailValid(email) {
    if (!email.length) return false;
    // Grabbed from https://emailregex.com/
    const emailRegex = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
    return !!emailRegex.test(email);
  }

  static searchLocation({ searchString, locations }) {
    if (searchString) {
      const searchUnits = searchString.split(/\s+/g);
      return locations.filter((location) => {
        let allSearchUnitsMatch = true;
        for (const unit of searchUnits) {
          const matchesAddress = location?.address?.toLowerCase().includes(unit.toLowerCase());
          const matchesLocationName = location?.name?.toLowerCase().includes(unit.toLowerCase());
          const matchesLocationTags = location.locationTags?.some((tag) => {
            const tagLabel = tag.split('/').pop().split(':')[1];
            return tagLabel.toLowerCase().includes(unit.toLowerCase());
          });
          if (!matchesAddress && !matchesLocationName && !matchesLocationTags) {
            allSearchUnitsMatch = false;
            break;
          }
        }
        return allSearchUnitsMatch;
      });
    }
    return locations;
  }

  /**
   * Filter an array of object/string/number/boolean by a user input search string.
   * You can specify an array of object properties that you want to match the search string.
   * @param {searchString} string the user input search string
   * @param {collection} array array of object/string/number/boolean that you want to filter
   * @param {props} array array of keys that belongs to the object of the collection, the properties you want to filter the collection by
   */
  static searchCollection({ searchString, collection, props, ignoreCase = true }) {
    if (!searchString) {
      return collection;
    }

    // If the search string contains spaces, separate the string into several units.
    // We want to filter the results that contains all the units.
    const searchUnits = searchString.split(' ').filter(Boolean);
    const normalizedUnits = ignoreCase ? searchUnits.map((u) => u.toLowerCase()) : searchUnits;

    return collection.filter((item) => {
      const getSearchableText = () => {
        if (typeof item === 'object') {
          return props.map((prop) => item[prop] ?? '').join(' ');
        }
        return String(item);
      };

      const text = ignoreCase ? getSearchableText().toLowerCase() : getSearchableText();
      return normalizedUnits.every((unit) => text.includes(unit));
    });
  }

  static sortObjectAlphabetically(object1, object2, key) {
    // Sort objects alphabetically according to specified property
    if (get(object1, key) && get(object2, key)) {
      // Trim whitespace that can affect comparison
      const comparableValue1 = get(object1, key).trim().toLowerCase();
      const comparableValue2 = get(object2, key).trim().toLowerCase();

      if (comparableValue1 < comparableValue2) {
        return -1;
      } else if (comparableValue1 > comparableValue2) {
        return 1;
      }
      return 0;
    }

    return 0;
  }

  static sortAlphabeticallyAndNumerically(one, two) {
    let a1;
    let b1;
    const rx = /(\d+)|(\D+)/g;
    const rd = /\d+/;
    const a = String(one).toLowerCase().match(rx);
    const b = String(two).toLowerCase().match(rx);
    while (a.length && b.length) {
      a1 = a.shift();
      b1 = b.shift();
      if (rd.test(a1) || rd.test(b1)) {
        if (!rd.test(a1)) return 1;
        if (!rd.test(b1)) return -1;
        if (a1 !== b1) return a1 - b1;
      } else if (a1 !== b1) return a1 > b1 ? 1 : -1;
    }
    return a.length - b.length;
  }

  /**
   * Uses canvas.measureText to compute and return the width of the given text
   * of given font in pixels
   *
   * @param {String} text - The text to be rendered
   * @param {String} font - The css font descriptor that text is to be rendered with (e.g. 'bold 14px verdana')
   */
  static getTextWidth(text, font) {
    if (!this.getTextWidth.canvas) {
      this.getTextWidth.canvas = document.createElement('canvas');
    }
    const canvas = this.getTextWidth.canvas;
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  /**
   * Fallback of the async clipboard API to copy text to clipboard
   * for better browser coverage
   * This uses the document.execCommand('copy') method which is deprecated
   */
  static fallbackCopyTextToClipboard({ string, successCallback, errorCallback }) {
    // http://stackoverflow.com/a/30810322/5652615
    const textArea = document.createElement('textarea');
    textArea.value = string;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        successCallback();
      }
    } catch {
      errorCallback();
      console.error('Unable to copy to clipboard');
    }
    document.body.removeChild(textArea);
  }

  /**
   * Use the async clipboard API to copy text to clipboard
   * with a fallback function for better browser coverage
   * Note: that Safari only allows clpying to clipboard within user gesture events
   */
  static copyStringToClipboard({ string, successCallback, errorCallback }) {
    if (!navigator.clipboard) {
      this.fallbackCopyTextToClipboard({ string, successCallback, errorCallback });
      return;
    }

    navigator.clipboard
      .writeText(string)
      .then(() => {
        successCallback();
      })
      .catch((error) => {
        errorCallback();
        console.error('Async: Could not copy text to clipboard: ', error);
      });
  }
}

export default CommonUtil;
